import { Position } from './Position';
import { Territory, TerritoryState } from './Territory';
import { Side } from './Side';
export const BOARD_SIZE = 11;
/**
 * Board Entity
 * Представляет игровое поле 11x11
 */
export class Board {
    _territories;
    _sides;
    _round;
    _currentSide;
    constructor() {
        this._territories = new Map();
        this._sides = new Map();
        this._round = 1;
        this._currentSide = 1; // Начинаем со стороны 1
        this.initializeBoard();
    }
    /**
     * Инициализация пустой доски
     */
    initializeBoard() {
        // Создаём все территории как нейтральные
        for (let y = 0; y < BOARD_SIZE; y++) {
            for (let x = 0; x < BOARD_SIZE; x++) {
                const position = new Position(x, y);
                const territory = new Territory(position);
                this._territories.set(position.toKey(), territory);
            }
        }
        // Создаём 10 сторон в элиминированном состоянии
        // Они будут активированы позже при инициализации игры
        for (let i = 0; i <= 9; i++) {
            const side = new Side(i);
            // Стороны создаются элиминированными и активируются только при необходимости
            this._sides.set(i, side);
        }
    }
    get territories() {
        return new Map(this._territories);
    }
    get sides() {
        return new Map(this._sides);
    }
    get round() {
        return this._round;
    }
    get currentSide() {
        return this._currentSide;
    }
    /**
     * Получение территории по позиции
     */
    getTerritoryAt(position) {
        return this._territories.get(position.toKey());
    }
    /**
     * Получение стороны по ID
     */
    getSide(sideId) {
        return this._sides.get(sideId);
    }
    /**
     * Было ли поле ходовым (активным) для стороны sideId в этот момент.
     *
     * Единая точка, через которую ВСЕ пути захвата решают, стоит клетка 30 очков
     * (ходовое поле врага) или 10 (обычная территория). Раньше каждый путь решал
     * сам: часть читала activeFields, часть хардкодила true/false — отсюда
     * расхождение, когда один и тот же захват давал то 10, то 30.
     */
    isActiveFieldOf(sideId, position) {
        return this._sides.get(sideId)?.isActiveField(position) ?? false;
    }
    /**
     * Получение текущей активной стороны
     */
    getCurrentSide() {
        return this._sides.get(this._currentSide);
    }
    /**
     * Получение всех нейтральных территорий
     */
    getNeutralTerritories() {
        return Array.from(this._territories.values()).filter(t => t.state === TerritoryState.NEUTRAL);
    }
    /**
     * Получение территорий стороны
     */
    getSideTerritories(sideId) {
        return Array.from(this._territories.values()).filter(t => t.owner === sideId);
    }
    /**
     * Проверка, окружена ли территория
     */
    isTerritorySurrounded(position) {
        const territory = this.getTerritoryAt(position);
        if (!territory)
            return false;
        const neighbors = position.getOrthogonalNeighbors();
        const requiredNeighbors = position.isBorder() ? neighbors.length : 4;
        let enemyNeighbors = 0;
        let wallNeighbors = 0;
        const currentOwner = territory.owner;
        for (const neighborPos of neighbors) {
            const neighbor = this.getTerritoryAt(neighborPos);
            if (neighbor && neighbor.state === TerritoryState.BURNED) {
                wallNeighbors++;
            }
            else if (neighbor && neighbor.owner !== null && neighbor.owner !== currentOwner) {
                enemyNeighbors++;
            }
        }
        return enemyNeighbors >= Math.max(0, requiredNeighbors - wallNeighbors);
    }
    /**
     * Получение доступных ходов с позиции
     * @param position - Позиция откуда ходить
     * @param sideId - ID стороны (опционально, для проверки возможности хода на свои территории)
     */
    getAvailableMovesFrom(position, sideId) {
        const moves = position.getKnightMoves();
        return moves.filter(pos => {
            const territory = this.getTerritoryAt(pos);
            return territory?.isAvailableForMovement(sideId) ?? false;
        });
    }
    /**
     * Переход к следующей стороне
     */
    nextTurn() {
        // Находим следующую активную сторону
        let nextSide = (this._currentSide + 1) % 10;
        let attempts = 0;
        while (attempts < 10) {
            const side = this._sides.get(nextSide);
            if (side?.isActive && side.canMove()) {
                this._currentSide = nextSide;
                return;
            }
            nextSide = (nextSide + 1) % 10;
            attempts++;
        }
        // Если не нашли активную сторону, игра окончена
        throw new Error('No active sides remaining');
    }
    /**
     * Переход к следующему раунду
     */
    nextRound() {
        this._round++;
        this._currentSide = 1; // Всегда начинаем с стороны 1
        // Обновляем состояние всех сторон для нового раунда
        for (const side of this._sides.values()) {
            if (side.isActive) {
                side.startNewRound();
                // КРИТИЧНО: После восстановления активных полей нужно поставить фигуры на них
                // Иначе MovementValidator будет отклонять ходы из-за "not occupied by this side"
                for (const activeFieldKey of side.turnActiveFields) {
                    const territory = this._territories.get(activeFieldKey);
                    if (territory && territory.owner === side.id && territory.occupiedBy !== side.id) {
                        territory.occupy(side.id);
                    }
                }
            }
        }
    }
    /**
     * Получение максимального количества ходов для текущего раунда
     */
    getMaxMovesPerRound() {
        if (this._round === 1)
            return 1;
        if (this._round === 2)
            return 3;
        return 3;
    }
    /**
     * Проверка, есть ли активные стороны
     */
    hasActiveSides() {
        for (const side of this._sides.values()) {
            if (side.isActive && side.canMove()) {
                return true;
            }
        }
        return false;
    }
    // ❌ Удалён Board.calculateScore (#B3): был плоский подсчёт (+1/+3, без 10/30),
    // расходился с дизайн-документом. Единственный источник правды по очкам —
    // ScoreService → ScoreCalculator (services/ScoreService.ts). Live-путь
    // (GameOrchestrator, server) уже использует ScoreService.
    /**
     * Клонирование доски
     */
    clone() {
        const cloned = new Board();
        // Клонируем территории
        cloned._territories = new Map();
        for (const [key, territory] of this._territories) {
            cloned._territories.set(key, territory.clone());
        }
        // Клонируем стороны
        cloned._sides = new Map();
        for (const [id, side] of this._sides) {
            cloned._sides.set(id, side.clone());
        }
        cloned._round = this._round;
        cloned._currentSide = this._currentSide;
        return cloned;
    }
    /**
     * Экспорт состояния доски
     */
    toState() {
        return {
            territories: new Map(this._territories),
            sides: new Map(this._sides),
            round: this._round,
            currentSide: this._currentSide,
        };
    }
    /**
     * Сериализация в JSON
     */
    toJSON() {
        return {
            territories: Array.from(this._territories.entries()).map(([key, territory]) => ({
                key,
                ...territory.toJSON(),
            })),
            sides: Array.from(this._sides.entries()).map(([id, side]) => ({
                id,
                ...side.toJSON(),
            })),
            round: this._round,
            currentSide: this._currentSide,
        };
    }
    /**
     * Восстановление состояния из JSON
     */
    static restoreFromJSON(data) {
        const board = new Board();
        // Восстанавливаем территории
        for (const territoryData of data.territories) {
            const territory = Territory.fromJSON(territoryData);
            board._territories.set(territoryData.key, territory);
        }
        // Восстанавливаем стороны
        for (const sideData of data.sides) {
            const side = Side.fromJSON(sideData);
            board._sides.set(sideData.id, side);
        }
        board._round = data.round;
        board._currentSide = data.currentSide;
        return board;
    }
    /**
     * Load board state directly from JSON in-place, bypassing the
     * restoreFromJSON → toState() → restoreState() copy chain.
     */
    loadFromJSON(data) {
        this._territories = new Map();
        for (const territoryData of data.territories) {
            this._territories.set(territoryData.key, Territory.fromJSON(territoryData));
        }
        this._sides = new Map();
        for (const sideData of data.sides) {
            this._sides.set(sideData.id, Side.fromJSON(sideData));
        }
        this._round = data.round;
        this._currentSide = data.currentSide;
    }
    /**
     * Восстановление состояния
     */
    restoreState(state) {
        this._territories = new Map(state.territories);
        this._sides = new Map(state.sides);
        this._round = state.round;
        this._currentSide = state.currentSide;
    }
    toString() {
        return `Board[round=${this._round}, currentSide=${this._currentSide}, activeSides=${Array.from(this._sides.values()).filter(s => s.isActive).length}]`;
    }
}
//# sourceMappingURL=Board.js.map